import React, { useState } from 'react';

import DealSplitModal from '@components/modals/DealSplitModal';

import ApiClient from '@lib/apiClient';

/** The query string that carries the deals the user chose to split to an order endpoint. */
export const dealSplitQuery = (splitDealIds) =>
	splitDealIds?.length > 0 ? `?splitDealIds=${splitDealIds.join(',')}` : '';

/**
 * Lets the user decide, before an order is placed or edited, whether the CRM deals it covers only part of are
 * split. Render `dealSplitModal` and await `askDealSplit` before calling the order endpoint.
 */
const useDealSplitPrompt = () => {
	const [prompt, setPrompt] = useState(null);

	/**
	 * Resolves to the ids of the deals to split ( empty when there is nothing to decide ), or to null when the
	 * user closed the modal and the order action should not go ahead.
	 * `productsToAdd` are the { dealId, productId } pairs the action is about to add to the order.
	 */
	const askDealSplit = async (orderId, { productsToAdd = [], submitting = false } = {}) => {
		let deals = [];
		try {
			const { data } = await ApiClient.POST(
				'getDealSplitCandidates',
				productsToAdd.filter((product) => product.dealId && product.productId),
				`${orderId}/deal-split-candidates?submitting=${submitting}`
			);
			deals = Array.isArray(data) ? data : [];
		} catch {
			// a deal is only ever split by the user's choice, so without the question nothing is split
		}
		if (deals.length === 0) return [];

		return new Promise((resolve) => setPrompt({ deals, resolve }));
	};

	const closePrompt = (splitDealIds) => {
		prompt.resolve(splitDealIds);
		setPrompt(null);
	};

	const dealSplitModal = prompt ? (
		<DealSplitModal deals={prompt.deals} onDecide={closePrompt} onCancel={() => closePrompt(null)} />
	) : null;

	return { askDealSplit, dealSplitModal };
};

export default useDealSplitPrompt;
